import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, 
  Plus, 
  Mail, 
  Edit, 
  Trash2, 
  Star,
  StarOff,
  Loader2,
  MessageSquare,
  Copy
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface MessageTemplate {
  id: string;
  template_name: string;
  subject: string | null;
  body: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

const AgentMessaging = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [formData, setFormData] = useState({
    template_name: '',
    subject: '',
    body: '',
    is_default: false,
  });

  // Get agent session
  const getAgentSession = () => {
    const sessionData = localStorage.getItem('foundingAgentSession') || sessionStorage.getItem('foundingAgentSession');
    if (sessionData) {
      try {
        return JSON.parse(sessionData);
      } catch {
        return null;
      }
    }
    return null;
  };

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      const agentSession = getAgentSession();
      if (!agentSession?.userId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('agent_message_templates')
          .select('*')
          .eq('agent_user_id', agentSession.userId)
          .order('is_default', { ascending: false })
          .order('created_at', { ascending: false });

        if (error) throw error;
        setTemplates(data || []);
      } catch (error) {
        console.error('Error fetching templates:', error);
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const agentSession = getAgentSession();
    if (!agentSession?.userId) {
      toast.error('Please log in to manage templates');
      return;
    }

    if (!formData.template_name.trim() || !formData.body.trim()) {
      toast.error('Template name and body are required');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        // Update existing template
        const { error } = await supabase
          .from('agent_message_templates')
          .update({
            template_name: formData.template_name,
            subject: formData.subject || null,
            body: formData.body,
            is_default: formData.is_default,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingTemplate.id);

        if (error) throw error;

        setTemplates(prev => prev.map(t => 
          t.id === editingTemplate.id 
            ? { ...t, ...formData, updated_at: new Date().toISOString() }
            : t
        ));
        toast.success('Template updated');
      } else {
        // Create new template
        const { data, error } = await supabase
          .from('agent_message_templates')
          .insert({
            agent_user_id: agentSession.userId,
            agent_user_type: 'internal',
            template_name: formData.template_name,
            subject: formData.subject || null,
            body: formData.body,
            is_default: formData.is_default,
          })
          .select()
          .single();

        if (error) throw error;

        setTemplates(prev => [data, ...prev]);
        toast.success('Template created');
      }

      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (template: MessageTemplate) => {
    const agentSession = getAgentSession();
    if (!agentSession?.userId) return;

    try {
      const { error } = await supabase
        .from('agent_message_templates')
        .delete()
        .eq('id', template.id);

      if (error) throw error;

      setTemplates(prev => prev.filter(t => t.id !== template.id));
      toast.success('Template deleted');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  // Toggle default status
  const toggleDefault = async (template: MessageTemplate) => {
    const agentSession = getAgentSession();
    if (!agentSession?.userId) return;

    try {
      // If setting as default, remove default from others first
      if (!template.is_default) {
        await supabase
          .from('agent_message_templates')
          .update({ is_default: false })
          .eq('agent_user_id', agentSession.userId);
      }

      const { error } = await supabase
        .from('agent_message_templates')
        .update({ is_default: !template.is_default })
        .eq('id', template.id);

      if (error) throw error;

      setTemplates(prev => prev.map(t => ({
        ...t,
        is_default: t.id === template.id ? !template.is_default : false
      })));
      
      toast.success(template.is_default ? 'Removed as default' : 'Set as default template');
    } catch (error) {
      console.error('Error updating default:', error);
      toast.error('Failed to update template');
    }
  };

  // Copy template to clipboard
  const copyTemplate = (template: MessageTemplate) => {
    const text = template.subject 
      ? `Subject: ${template.subject}\n\n${template.body}`
      : template.body;
    navigator.clipboard.writeText(text);
    toast.success('Template copied to clipboard');
  };

  // Reset form
  const resetForm = () => {
    setFormData({ template_name: '', subject: '', body: '', is_default: false });
    setEditingTemplate(null);
  };

  // Open edit dialog
  const openEditDialog = (template: MessageTemplate) => {
    setEditingTemplate(template);
    setFormData({
      template_name: template.template_name,
      subject: template.subject || '',
      body: template.body,
      is_default: template.is_default,
    });
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/agent/dashboard')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Message Templates</h1>
            <p className="text-muted-foreground">
              Create and manage your outreach templates for athletes
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>
                    {editingTemplate ? 'Edit Template' : 'Create New Template'}
                  </DialogTitle>
                  <DialogDescription>
                    Create a reusable message template for athlete outreach
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="template_name">Template Name *</Label>
                    <Input
                      id="template_name"
                      placeholder="e.g., Initial Outreach, Follow-up, NIL Opportunity"
                      value={formData.template_name}
                      onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject Line</Label>
                    <Input
                      id="subject"
                      placeholder="e.g., Exciting NIL Opportunity"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="body">Message Body *</Label>
                    <Textarea
                      id="body"
                      placeholder="Write your message template here. You can use placeholders like {athlete_name}, {sport}, etc."
                      value={formData.body}
                      onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                      rows={8}
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Tip: Use placeholders like {'{athlete_name}'}, {'{sport}'}, {'{team}'} for personalization
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Loading templates...</span>
          </div>
        ) : templates.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Mail className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No Templates Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first message template to streamline athlete outreach
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create First Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card key={template.id} className="hover:border-primary/30 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{template.template_name}</CardTitle>
                        {template.is_default && (
                          <Badge variant="secondary" className="text-xs">
                            <Star className="h-3 w-3 mr-1 fill-current" />
                            Default
                          </Badge>
                        )}
                      </div>
                      {template.subject && (
                        <CardDescription className="mt-1">
                          Subject: {template.subject}
                        </CardDescription>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                    {template.body}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => toggleDefault(template)}
                        title={template.is_default ? 'Remove as default' : 'Set as default'}
                      >
                        {template.is_default ? (
                          <StarOff className="h-4 w-4" />
                        ) : (
                          <Star className="h-4 w-4" />
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => copyTemplate(template)}
                        title="Copy template"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditDialog(template)}
                        title="Edit template"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Template?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete "{template.template_name}". This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(template)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default AgentMessaging;
